import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DicebearAvatarConfig } from '../dto/dicebear-avatar-config.dto';
import { ProcessResult, QueryResult } from '../components/common-dto/query.dto';
import { Avatar } from 'primeng/avatar';
import { AvatarDto } from '../dto/avatar.dto';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { AuthService } from '../services/auth.service';
import { BackendService } from '../services/backend.service';

@Injectable({
  providedIn: 'root',
})
export class MockAvatarService {
  private apiUrl = 'https://api.dicebear.com/9.x/avataaars/svg';

  constructor(
    private http: HttpClient,
    private backend: BackendService,
    private authService: AuthService
  ) {}

  // Mock SVG response for avatar image
  private mockSvgResponse = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="40" stroke="black" stroke-width="3" fill="red" /></svg>`;

  // Mock avatar data
  private mockAvatars: QueryResult<AvatarDto> = {
    items: [
      {
        id: 'mock-user-id',
        item: {
          avatarAsBase64: '',
          jsonProps: {
            seed: 'avatar1',
            backgroundColor: 'b6e3f4',
            skinColor: 'FFDBB0',
            hairColor: '100000',
            accessories: 'prescription02',
            accessoriesColor: '100000',
            facialHair: 'beardMedium',
            facialHairColor: '100000',
            facialHairProbability: 100,
            hatColor: '100000',
            clothing: 'hoodie',
            clothesColor: '00FF00',
            eyebrows: 'default',
            eyes: 'default',
            top: 'bob',
            mouth: 'default',
            style: 'circle',
          },
          createdAt: new Date('2023-01-01'),
          updatedAt: new Date('2023-01-01'),
          userId: 'user1',
          tenantId: 'tenant1',
        },
      },
      {
        id: 'avatar2',
        item: {
          avatarAsBase64: '',
          jsonProps: {
            seed: 'avatar1',
            backgroundColor: ['b6e3f4'],
            skinColor: 'FFDBB0',
            hairColor: '000000',
            accessories: 'round',
            accessoriesColor: '000000',
            facialHair: 'beardMedium',
            facialHairColor: '100000',
            facialHairProbability: 100,
            hatColor: '100000',
            clothing: 'hoodie',
            clothesColor: '00FF00',
            eyebrows: 'default',
            eyes: 'default',
            top: 'bob',
            mouth: 'default',
            style: 'circle',
          },
          createdAt: new Date('2023-02-15'),
          updatedAt: new Date('2023-02-15'),
          userId: 'user2',
          tenantId: 'tenant1',
        },
      },
    ],
    total: 2,
    take: 100,
    skip: 0,
  };

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    return headers;
  }

  private stripHash(color: string | undefined): string | undefined {
    return color && color.startsWith('#') ? color.substring(1) : color;
  }

  getImage(options: DicebearAvatarConfig = {}): Observable<any> {
    
    // strip the # symbol from color values
    const strippedOptions: DicebearAvatarConfig = {
      ...options,
      accessoriesColor: this.stripHash(options.accessoriesColor),
      clothesColor: this.stripHash(options.clothesColor),
      facialHairColor: this.stripHash(options.facialHairColor),
      hairColor: this.stripHash(options.hairColor),
      hatColor: this.stripHash(options.hatColor),
      skinColor: this.stripHash(options.skinColor),
    };

    // filter out blank values so they are not sent as query parameters
    // we need to allow for 0 / false values though
    const filteredOptions = Object.fromEntries(
      Object.entries(strippedOptions).filter(([_, value]) => value !== null && value !== undefined && value !== '')
    );

    const params = new HttpParams({
      fromObject: filteredOptions as Record<string, string>,
    });
    return this.http.get(this.apiUrl + (params ? '?' + params : ''), {
      headers: this.getHeaders(),
      responseType: 'text',
    });
  }

  getAvatars(): Observable<QueryResult<AvatarDto>> {
    return of(this.mockAvatars);
  }

  getAvatar(id: string): Observable<AvatarDto> {
    const avatars = this.mockAvatars.items.filter((a) => a.id === id);
    return of(avatars ? avatars[0].item : ({} as AvatarDto));
  }

  createAvatar(avatar: AvatarDto): Observable<ProcessResult> {
    return of({
      success: true,
      message: 'Avatar created successfully',
      id: 'new-avatar-' + Math.random().toString(36).substr(2, 9),
    });
  }

  updateAvatar(id: string, avatar: AvatarDto): Observable<ProcessResult> {
    this.authService.setAvatar(avatar.avatarAsBase64);
    return of({
      success: true,
      message: 'Avatar updated successfully',
      id: id,
    });
  }
}
